'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePortalAuth } from '@/contexts/PortalAuthContext';

export default function PortalSignupPage() {
  const { signUp } = usePortalAuth();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match.'); return; }
    if (form.password.length < 3) { setError('Password must be at least 3 characters.'); return; }
    setLoading(true);
    try {
      await signUp({
        username: form.username,
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        password: form.password,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'linear-gradient(rgba(10,191,188,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(10,191,188,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} />
        <div className="relative w-full max-w-md text-center">
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-10 shadow-2xl">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#0ABFBC]/20 border-2 border-[#0ABFBC]/40 mb-6">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                <path d="M20 6L9 17l-5-5" stroke="#0ABFBC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Request Submitted!</h2>
            <p className="text-gray-300 text-base mb-3 leading-relaxed">
              Your registration request has been submitted successfully.
            </p>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              An administrator will review your request before your account is activated. You will be contacted once your account is approved.
            </p>
            <Link
              href="/portal/login"
              className="inline-block w-full bg-[#0ABFBC] hover:bg-[#089A97] text-white font-bold py-4 px-8 rounded-xl transition-all text-base shadow-lg shadow-[#0ABFBC]/20"
            >
              Back to Sign In
            </Link>
          </div>
          <div className="mt-6">
            <Link href="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition-colors group">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-0.5 transition-transform">
                <path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/>
              </svg>
              Back to Website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4 py-10">
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(10,191,188,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(10,191,188,0.3) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />

      <div className="relative w-full max-w-md">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 border-[#0ABFBC]/40 shadow-lg shadow-[#0ABFBC]/20">
              <Image
                src="/assets/images/app_logo.png"
                alt="Rio Grande Property Solutions logo"
                fill
                className="object-cover"
                onError={() => {}}
              />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Rio Grande Property Solutions</h1>
          <p className="text-[#0ABFBC] text-sm font-semibold mt-1 tracking-wide uppercase">Employee Portal</p>
        </div>

        <div className="bg-[#111111] border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Title */}
          <div className="mb-5 text-center">
            <h2 className="text-2xl font-bold text-white">Employee Registration</h2>
          </div>

          {/* Notice banner */}
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <p className="text-amber-300 text-sm text-center leading-relaxed">
              This registration page is for Rio Grande Property Solutions employees only. Accounts must be approved by an administrator before access is granted.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First & Last Name row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  required
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1.5">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Smith"
                  required
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Email Address</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(505) 000-0000"
                required
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Desired Username</label>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="jsmith"
                required
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Password</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-[#0ABFBC]/60 focus:ring-1 focus:ring-[#0ABFBC]/30 transition-all text-base"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0ABFBC] hover:bg-[#089A97] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-all duration-200 mt-2 shadow-lg shadow-[#0ABFBC]/20 text-base hover:shadow-[#0ABFBC]/40 hover:scale-[1.01]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Submitting...
                </span>
              ) : 'Request Employee Account'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-gray-500 text-sm">
              Already have an account?{' '}
              <Link href="/portal/login" className="text-[#0ABFBC] hover:text-[#089A97] font-medium transition-colors">
                Sign In
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link href="/" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition-colors group">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="group-hover:-translate-x-0.5 transition-transform">
              <path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/>
            </svg>
            Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
}
